/**
 * @module RenderDataPoint
 */

import { SerializationContext, Version } from 'adaptivecards-v2';

import ExtendedActionDataPoint from './ExtendedActionDataPoint';
import {
    MessageContext,
    ElementDetails,
    CardLoadPerfCheckpoints,
    CardDetails,
} from 'owa-actionable-message-v2';
import CardType from '../utils/CardType';
import { trace } from 'owa-trace';

/**
 * @class RenderDataPoint
 */

export default class RenderDataPoint extends ExtendedActionDataPoint {
    /**
     * @private
     * To check whether the version is ACv2 or not
     * @param {string} version - A version string
     * @returns whether the version is ACv2 or not
     */

    private isACv2Card(version: string): boolean {
        const ACV2_INITIAL_VERSION = '1.4';
        let isACv2 = false;
        if (version) {
            const context = new SerializationContext();
            const cardVersion = Version.parse(version, context);
            const baseVersion = Version.parse(ACV2_INITIAL_VERSION, context);
            try {
                isACv2 = cardVersion.compareTo(baseVersion) >= 0;
            } catch (e) {}
        }
        return isACv2;
    }

    /**
     * @private
     * To set Render data point properties
     * @param {MessageContext} messageContext - A message context object
     * @param {CardDetails} cardDetails - A card details object
     * @param {any} [actionContext] - An action context object
     * @returns Nothing
     */

    private setRenderProperties(
        messageContext: MessageContext,
        cardDetails: CardDetails,
        actionContext?: any
    ): void {
        if (messageContext?.isGroupEscalationMessage) {
            this.setProperty('isGroupEscalationMessage', messageContext.isGroupEscalationMessage);
        }
        if (messageContext?.conversationId) {
            this.setProperty('conversationId', messageContext.conversationId);
        }

        /**
         * No need to check for ACv2 control tower flag, this is handled in ActionableMessageCardWrapper.tsx
         */
        this.setProperty(
            'messageType',
            cardDetails.isAdaptiveCard
                ? this.isACv2Card(cardDetails?.card?.version)
                    ? CardType.AdaptiveCardV2
                    : CardType.AdaptiveCard
                : CardType.ExplicitMessageCard
        );

        if (messageContext?.mailboxInfo?.type) {
            this.setProperty('isGroupMailbox', messageContext.mailboxInfo.type === 'GroupMailbox');
        }

        if (cardDetails?.card?.originator) {
            this.setProperty('originator', cardDetails.card.originator);
        }

        if (cardDetails?.connectorSenderGuid) {
            this.setProperty('connectorSenderGuid', cardDetails.connectorSenderGuid);
        }

        if (cardDetails?.card?.messageCardContext) {
            try {
                const messageCardContext =
                    typeof cardDetails.card.messageCardContext === 'string'
                        ? cardDetails.card.messageCardContext
                        : JSON.stringify(cardDetails.card.messageCardContext);
                this.setProperty('messageCardContext', messageCardContext);
            } catch (error) {
                trace.info(
                    `[ActionableMessageDebug] RenderDataPoint - Error while JSON.stringify messageCardContext: ${error}`
                );
            }
        }

        if (cardDetails?.messageCardExtensionContext) {
            try {
                const messageCardExtensionContext =
                    typeof cardDetails.messageCardExtensionContext === 'string'
                        ? cardDetails.messageCardExtensionContext
                        : JSON.stringify(cardDetails.messageCardExtensionContext);
                this.setProperty('messageCardExtensionContext', messageCardExtensionContext);
            } catch (error) {
                trace.info(
                    `[ActionableMessageDebug] RenderDataPoint - Error while JSON.stringify messageCardExtensionContext: ${error}`
                );
            }
        }

        if (actionContext) {
            try {
                this.setProperty(
                    'actionContext',
                    typeof actionContext === 'string'
                        ? actionContext
                        : JSON.stringify(actionContext)
                );
            } catch (error) {
                trace.info(
                    `[ActionableMessageDebug] RenderDataPoint - Error while JSON.stringify actionContext: ${error}`
                );
            }
        }
    }

    /**
     * @constructor
     * To create an instance of Render data point
     * @param {string} eventName - A client event name
     * @param {MessageContext} messageContext - A message context object
     * @param {CardDetails} cardDetails - A card details object
     * @param {ElementDetails} [elementDetails] - An element details object
     * @param {any} [actionContext] - An action context object
     * @param {CardLoadPerfCheckpoints} [perfCheckpoints] - The data check points of the card performance
     * @returns An instance of RenderDataPoint
     */

    constructor(
        eventName: string,
        messageContext: MessageContext,
        cardDetails: CardDetails,
        elementDetails?: ElementDetails,
        actionContext?: any,
        perfCheckPoints?: CardLoadPerfCheckpoints
    ) {
        super(eventName, messageContext, elementDetails, perfCheckPoints);
        this.setRenderProperties(messageContext, cardDetails, actionContext);
    }
}
